/**
 * Costos: lo que Twilio facturó por día (Usage Records, barras apiladas por grupo, sin `total`)
 * con la línea de lo que estimaba nuestra tabla, y la conciliación del mes: Twilio facturó /
 * nuestra tabla estimaba / cobrado a la marca.
 *
 * Nada de esto cambia un cobro: lo que se descuenta del saldo de la marca sigue saliendo de la
 * tabla de tarifas (fail-closed). Por eso la pantalla dice siempre que la documentación de Twilio no
 * aclara si el precio incluye la tarifa de Meta (§8 D-1).
 */

import { Bar, CartesianGrid, ComposedChart, Line, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import type { EstadisticasMarca, EstadisticasTwilio, FilaDia } from "../../types.estadisticas";
import {
  ACLARACION_COSTO_TWILIO, COLORES, aclaracionUso, etiquetaGrupoUso, gruposPresentes, pctConSigno, serieCostos, textoConciliacion,
} from "../../logic/estadisticas";
import { usd } from "../../logic/formato";
import { num } from "../../../campana-nueva/utils/format";
import { AvisoConectar, ESTILO_TOOLTIP, Leyenda, Vacio } from "./comun";

function Cifra({ label, valor }: { label: string; valor: string }) {
  return (
    <div className="rounded-xl bg-slate-50 px-3 py-2.5">
      <p className="text-lg font-semibold text-slate-900">{valor}</p>
      <p className="text-xs text-slate-600">{label}</p>
    </div>
  );
}

export function TarjetaCostos({ twilio, serie, costoMarca }: {
  twilio: EstadisticasTwilio; serie: FilaDia[] | null; costoMarca?: EstadisticasMarca["costo"] | null;
}) {
  if (!twilio.disponible) return <AvisoConectar motivo={twilio.motivoNoDisponible} />;
  const datos = serieCostos(twilio.usos?.serie, serie);
  const grupos = gruposPresentes(datos);
  const hayCosto = datos.some((f) => (f.costoPropioUSD ?? 0) > 0);
  const c = twilio.conciliacionMes;
  const dif = pctConSigno(c?.diferenciaPct);
  const mes = (twilio.usos?.mes || []).filter((u) => u.grupo !== "gratis" || (Number(u.count) || 0) > 0);

  return (
    <div className="space-y-5">
      {!grupos.length && !hayCosto ? (
        <Vacio>Twilio todavía no informó uso en estas fechas. Tocá «Actualizar datos» para traerlo.</Vacio>
      ) : (
        <div className="space-y-3">
          <Leyenda items={[
            ...grupos.map((g) => ({ color: g.color, label: etiquetaGrupoUso(g.grupo) })),
            ...(hayCosto ? [{ color: COLORES.costoPropio, label: "Lo que estimaba nuestra tabla", linea: true }] : []),
          ]} />
          <div style={{ width: "100%", height: 220 }}>
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart data={datos} margin={{ top: 8, right: 8, bottom: 0, left: -4 }}>
                <CartesianGrid stroke={COLORES.grilla} vertical={false} />
                <XAxis dataKey="etiqueta" tick={{ fontSize: 11, fill: COLORES.eje }} tickLine={false} axisLine={false} minTickGap={16} />
                <YAxis tick={{ fontSize: 11, fill: COLORES.eje }} tickLine={false} axisLine={false} width={56}
                  tickFormatter={(v) => `$${Number(v).toLocaleString("es-AR", { maximumFractionDigits: 2 })}`} />
                <Tooltip {...ESTILO_TOOLTIP} formatter={(v, n) => [usd(Number(v), 4), String(n)]} />
                {grupos.map((g, i) => (
                  <Bar key={g.grupo} dataKey={g.grupo} name={etiquetaGrupoUso(g.grupo)} stackId="uso" fill={g.color}
                    stroke="#fff" strokeWidth={1} radius={i === grupos.length - 1 ? [4, 4, 0, 0] : 0} isAnimationActive={false} />
                ))}
                {hayCosto && (
                  <Line type="monotone" dataKey="costoPropioUSD" name="Lo que estimaba nuestra tabla" stroke={COLORES.costoPropio} strokeWidth={2}
                    dot={false} connectNulls={false} isAnimationActive={false} />
                )}
              </ComposedChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      <div>
        <h4 className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2">Conciliación del mes</h4>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
          <Cifra label="Twilio facturó (WhatsApp)" valor={c?.usoWhatsappUSD === null || c?.usoWhatsappUSD === undefined ? "Sin dato" : usd(c.usoWhatsappUSD)} />
          <Cifra label="Nuestra tabla estimaba" valor={usd(c?.costoPropioUSD)} />
          <Cifra label="Cobrado a la marca" valor={usd(c?.cobradoClienteUSD)} />
        </div>
        <p className="mt-2 text-sm text-slate-700">
          {dif ? <>Diferencia entre Twilio y nuestra tabla: <strong>{dif}</strong>.</> : "Todavía no hay con qué comparar la factura de Twilio."}
        </p>
        {c?.aclaracion && <p className="mt-1 text-xs text-slate-500">{c.aclaracion}</p>}
      </div>

      {costoMarca?.conciliacion && (
        <div>
          <h4 className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-1">Precio por mensaje según Twilio (en estas fechas)</h4>
          <p className="text-sm text-slate-700">{textoConciliacion(costoMarca.conciliacion)}</p>
        </div>
      )}

      {mes.length > 0 && (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <caption className="text-left text-xs font-bold uppercase tracking-wider text-slate-500 mb-2">Uso del mes por categoría</caption>
            <thead>
              <tr className="text-left text-[11px] font-bold uppercase tracking-wider text-slate-400">
                <th scope="col" className="py-2 pr-3">Categoría</th>
                <th scope="col" className="py-2 pr-3 text-right">Cantidad</th>
                <th scope="col" className="py-2 text-right">Precio</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {mes.map((u) => (
                <tr key={u.categoria}>
                  <td className="py-2 pr-3 text-slate-800">{u.etiqueta || etiquetaGrupoUso(u.grupo)}<span className="ml-1.5 font-mono text-[10px] text-slate-400">{u.categoria}</span></td>
                  <td className="py-2 pr-3 text-right tabular-nums text-slate-700">{u.count === null ? "—" : `${num(u.count)} ${u.countUnit || ""}`.trim()}</td>
                  <td className="py-2 text-right tabular-nums text-slate-900">{u.price === null ? "—" : `${usd(u.price, 4)}${u.priceUnit && u.priceUnit.toUpperCase() !== "USD" ? ` (${u.priceUnit})` : ""}`}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="space-y-1 text-xs text-slate-400 leading-relaxed">
        <p>{ACLARACION_COSTO_TWILIO}</p>
        <p>{aclaracionUso(twilio.usos?.asOf)}</p>
      </div>
    </div>
  );
}

export default TarjetaCostos;
